import { useEffect } from 'react';
import L from 'leaflet';
import { getGeoTIFFTile } from '../services/api';

export const createGeoTIFFTileLayer = (geoTIFFData, onError) => {
  const requests = new Map();
  const [west, south, east, north] = geoTIFFData.bbox;
  let errorReported = false;
  const RasterTiles = L.GridLayer.extend({
    createTile(coords, done) {
      const tile = document.createElement('img');
      tile.alt = '';
      tile.setAttribute('role', 'presentation');
      const request = { controller: new AbortController(), url: null };
      requests.set(tile, request);

      getGeoTIFFTile(geoTIFFData.filename, geoTIFFData.version, coords, request.controller.signal)
        .then((blob) => {
          if (request.controller.signal.aborted) return;
          request.url = URL.createObjectURL(blob);
          tile.onload = () => {
            if (!request.controller.signal.aborted) done(null, tile);
          };
          tile.onerror = () => reportError(new Error('Cannot decode GeoTIFF tile'));
          tile.src = request.url;
        })
        .catch(reportError);

      function reportError(error) {
        if (request.controller.signal.aborted) return;
        console.error('Error loading GeoTIFF tile:', error);
        done(error, tile);
        if (!errorReported) {
          errorReported = true;
          onError?.(error);
        }
      }

      return tile;
    },
  });
  const layer = new RasterTiles({
    bounds: [[south, west], [north, east]],
    tileSize: geoTIFFData.tileSize,
    maxZoom: geoTIFFData.maxZoom,
    noWrap: true,
    pane: 'geotiffPane',
    className: 'geotiff-overlay',
    keepBuffer: 1,
  });
  const release = (tile) => {
    const request = requests.get(tile);
    if (!request) return;
    request.controller.abort();
    tile.onload = null;
    tile.onerror = null;
    if (request.url) URL.revokeObjectURL(request.url);
    requests.delete(tile);
  };
  layer.on('tileunload', ({ tile }) => release(tile));
  layer.on('remove', () => [...requests.keys()].forEach(release));
  return layer;
};

const GeoTIFFLayer = ({ geoTIFFData, map, onError }) => {
  useEffect(() => {
    if (!map || !geoTIFFData) return;
    const pane = map.getPane('geotiffPane') || map.createPane('geotiffPane');
    pane.style.zIndex = 250;
    pane.style.pointerEvents = 'none';
    const layer = createGeoTIFFTileLayer(geoTIFFData, onError);
    layer.addTo(map);
    return () => layer.remove();
  }, [map, geoTIFFData, onError]);

  return null;
};

export default GeoTIFFLayer;
